import { useCallback, useEffect, useRef } from 'react';
import type { ComposeInput, ID } from '@proofroom/schema';
import { createComposer, type Composer } from './composer';
import { useViewer } from '../state/viewerStore';

/**
 * Debounced per-story composition through the worker; stale results are dropped. `cancel`
 * drops whatever is pending or in flight for a story, for the caller who has decided the
 * story needs no engine lines at all: a later compose() supersedes an earlier one on its own,
 * but nothing else does, so a compose the last update did not want would otherwise land.
 */
export function useCompose(debounceMs = 40) {
  const composer = useRef<Composer | null>(null);
  const timers = useRef(new Map<ID, number>());
  const versions = useRef(new Map<ID, number>());
  const setComposed = useViewer((s) => s.setComposed);
  const setComposing = useViewer((s) => s.setComposing);
  const setComposeError = useViewer((s) => s.setComposeError);

  useEffect(() => {
    composer.current = createComposer();
    const t = timers.current;
    return () => { composer.current?.dispose(); composer.current = null; for (const id of t.values()) clearTimeout(id); t.clear(); };
  }, []);

  const compose = useCallback((storyId: ID, input: ComposeInput) => {
    const prev = timers.current.get(storyId);
    if (prev) clearTimeout(prev);
    const handle = window.setTimeout(async () => {
      timers.current.delete(storyId);
      const c = composer.current;
      if (!c) return;
      const v = (versions.current.get(storyId) ?? 0) + 1;
      versions.current.set(storyId, v);
      setComposing(storyId, true);
      try {
        const r = await c.compose(input);
        if (versions.current.get(storyId) !== v) return;
        setComposed(storyId, r.output);
        setComposeError(storyId, undefined);
      } catch (err) {
        console.warn('[compose] failed', err);
        if (versions.current.get(storyId) === v) setComposeError(storyId, err instanceof Error ? err.message : String(err));
      } finally {
        if (versions.current.get(storyId) === v) setComposing(storyId, false);
      }
    }, debounceMs);
    timers.current.set(storyId, handle);
  }, [debounceMs, setComposed, setComposing, setComposeError]);

  const cancel = useCallback((storyId: ID) => {
    const prev = timers.current.get(storyId);
    if (prev) { clearTimeout(prev); timers.current.delete(storyId); }
    // a bumped version is what makes an in-flight result stale when it arrives
    versions.current.set(storyId, (versions.current.get(storyId) ?? 0) + 1);
    setComposing(storyId, false);
  }, [setComposing]);

  return { compose, cancel };
}
